
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQSection = () => {
  return (
    <section id="faq" className="section-padding scroll-section">
      <div className="container max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
          Any Questions? We Got You Covered
        </h2>
        <p className="text-center text-muted-foreground mb-12">
          Here are some <strong className="font-semibold text-gray-900">frequently asked questions.</strong>
        </p>
        
        <Accordion type="single" collapsible className="w-full faq-accordion">
          <AccordionItem value="item-1">
            <AccordionTrigger className="md:text-left text-center">
              <span className="flex-1 text-center md:text-left">What is CodeRide?</span>
            </AccordionTrigger>
            <AccordionContent>
              CodeRide is a tool that makes your code <strong className="font-semibold text-gray-900">transparent to AI</strong>, letting it understand your projects without you having to explain it again and again.
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-2">
            <AccordionTrigger className="md:text-left text-center">
              <span className="flex-1 text-center md:text-left">What is "vibe coding" and how does CodeRide enhance it?</span>
            </AccordionTrigger>
            <AccordionContent>
              "Vibe coding" refers to the collaborative flow of working with AI coding assistants and AI code editors. CodeRide enhances this experience by adding structure while maintaining flexibility. It transforms your casual coding sessions with AI into a <strong className="font-semibold text-gray-900">disciplined, contextual workflow</strong> where your AI assistant consistently understands your project goals, constraints, and previous decisions.
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-3">
            <AccordionTrigger className="md:text-left text-center">
              <span className="flex-1 text-center md:text-left">How does CodeRide solve the problem of context reset between AI sessions?</span>
            </AccordionTrigger>
            <AccordionContent>
              Traditional AI coding assistants forget everything between chat sessions. CodeRide maintains a <strong className="font-semibold text-gray-900">persistent project memory</strong> that gets passed along with each task, eliminating the need to repeatedly explain your project structure, requirements, or decisions to your AI assistant.
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-4">
            <AccordionTrigger className="md:text-left text-center">
              <span className="flex-1 text-center md:text-left">How does CodeRide maintain context between AI interactions?</span>
            </AccordionTrigger>
            <AccordionContent>
              CodeRide creates a persistent knowledge base for your project that prevents the "memory loss" problem typical with AI coding assistants. It tracks decisions, architecture patterns, and implementation details across all tasks, ensuring your AI assistant maintains complete project understanding regardless of token limitations.
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-5">
            <AccordionTrigger className="md:text-left text-center">
              <span className="flex-1 text-center md:text-left">How is CodeRide different from standard vibe coding sessions?</span>
            </AccordionTrigger>
            <AccordionContent>
              Unlike regular vibe coding sessions where you need to explain between tasks or spend your context window to read your documentations multiple times, CodeRide provides the AI with a comprehensive understanding of your entire project, resulting in more contextually accurate and useful code suggestions. CodeRide also maintains knowledge continuity across multiple coding sessions, eliminating the repetitive explanations typically required.
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-6">
            <AccordionTrigger className="md:text-left text-center">
              <span className="flex-1 text-center md:text-left">How does CodeRide handle task management?</span>
            </AccordionTrigger>
            <AccordionContent>
              CodeRide transforms standard tasks into AI-optimized formats that preserve essential context. You can import tasks manually or via documents, and CodeRide will structure them to maximize AI comprehension while maintaining project context.
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-7">
            <AccordionTrigger className="md:text-left text-center">
              <span className="flex-1 text-center md:text-left">What programming languages does CodeRide support?</span>
            </AccordionTrigger>
            <AccordionContent>
              CodeRide supports all major programming languages including JavaScript, TypeScript, Python, Java, C#, Ruby, Go, PHP, and many more. Our AI adapts to any language in your codebase.
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-8">
            <AccordionTrigger className="md:text-left text-center">
              <span className="flex-1 text-center md:text-left">Which AI coding editors and IDEs does CodeRide support?</span>
            </AccordionTrigger>
            <AccordionContent>
              CodeRide supports popular IDEs including VS Code with Cursor, Windsurf and VS Code with Cline or GitHub Copilot. It also integrates with AI assistants like Claude desktop.
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-9">
            <AccordionTrigger className="md:text-left text-center">
              <span className="flex-1 text-center md:text-left">How does CodeRide integrate with AI-native code editors like Cursor, Windsurf, and Cline?</span>
            </AccordionTrigger>
            <AccordionContent>
              CodeRide seamlessly connects with AI-native editors through our MCP. Your editor's AI assistant can directly access CodeRide's functions like get_project, get_task, and update_project to maintain complete context. Simply point your AI assistant to the appropriate task, and CodeRide handles all context management behind the scenes. All project knowledge and instructions are pre-optimized for AI consumption, eliminating repetitive explanations while enhancing your coding experience.
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-10">
            <AccordionTrigger className="md:text-left text-center">
              <span className="flex-1 text-center md:text-left">How does CodeRide help with larger projects with multiple components?</span>
            </AccordionTrigger>
            <AccordionContent>
              For larger projects, CodeRide creates a project knowledge graph that helps your AI assistant understand relationships between different components. This ensures consistent implementation patterns across the entire project, even when working on separate modules or features.
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-11">
            <AccordionTrigger className="md:text-left text-center">
              <span className="flex-1 text-center md:text-left">Can I import existing task lists into CodeRide?</span>
            </AccordionTrigger>
            <AccordionContent>
              Yes, CodeRide supports batch importing of tasks from documents and transforms them into AI-optimized formats. This makes it easy to continue working on existing projects while taking advantage of CodeRide's context preservation features.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>
  );
};

export default FAQSection;
